import axiosClient from "./axiosClient";

// Interface for daily users data
export interface DailyUserData {
  date: string;
  users: number;
}

// Interface for country data
export interface CountryData {
  country: string;
  users: number;
}

// Interface for referral stats response
export interface ReferralStats {
  dailyUsersLast7Days: DailyUserData[];
  totalUsers: number;
  usersByCountry: CountryData[];
}

// Interface for user profile and coins
export interface UserProfile {
  totalCoins: number;
  availableCoins: number;
  usedCoins: number;
  premiumStatus: 'free' | 'premium' | 'earned_premium';
  achievements: string[];
  taskCompletionHistory: TaskHistory[];
}

// Interface for task history
export interface TaskHistory {
  taskId: string;
  completedAt: string;
  coinsEarned: number;
  verificationStatus: 'pending' | 'approved' | 'rejected';
}

// Interface for coin transactions
export interface CoinTransaction {
  id: number;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  source: string;
  timestamp: string;
  settlementTime: string;
}

// Interface for coin transactions response
export interface CoinTransactionsResponse {
  transactions: CoinTransaction[];
}

// Interface for coin balance response
export interface CoinBalanceResponse {
  balance: number;
}

// Interface for task status
export type TaskStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";

// Interface for task type (from task-types API)
export interface TaskType {
  identifier: string;
  displayName: string;
  minRewardValue: number;
  maxRewardValue: number;
  verificationTime: number;
}

// Interface for task types response
export interface TaskTypesResponse {
  taskTypes: TaskType[];
}

// Interface for a single task
export interface Task {
  taskId: string;
  status: TaskStatus;
  minRewardValue: number;
  maxRewardValue: number;
  rewardValue: number;
  verificationDueAt: string | null;
}

// Interface for tasks response
export interface TasksResponse {
  tasks: Task[];
}

// Interface for task claim request
export interface TaskClaimRequest {
  taskId: string;
  verificationData?: {
    postUrl?: string;
  };
}

// Interface for task claim response
export interface TaskClaimResponse {
  success: boolean;
  message: string;
  coinsEarned?: number;
  newBalance?: number;
}

/**
 * Fetch the referral code for the current user
 * @returns {Promise<string>} The referral code
 */
export const getReferralCode = async (): Promise<string> => {
  try {
    const response = await axiosClient.get<string>("/api/analytics/ref");
    return response.data;
  } catch (error) {
    console.error("Error fetching referral code:", error);
    throw error;
  }
};

/**
 * Fetch referral statistics for the current user
 * @returns {Promise<ReferralStats>} The referral statistics
 */
export const getReferralStats = async (): Promise<ReferralStats> => {
  try {
    const response = await axiosClient.get<ReferralStats>(
      "/api/analytics/ref/stats"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching referral stats:", error);
    throw error;
  }
};

/**
 * Fetch all available task types
 * @returns {Promise<TaskType[]>} The task types data
 */
export const getTaskTypes = async (): Promise<TaskType[]> => {
  try {
    const response = await axiosClient.get<TaskTypesResponse>("/api/v1/task-types");
    return response.data.taskTypes;
  } catch (error) {
    console.error("Error fetching task types:", error);
    throw error;
  }
};

/**
 * Fetch all available tasks for the current user
 * @returns {Promise<TasksResponse>} The tasks data
 */
export const getTasks = async (): Promise<TasksResponse> => {
  try {
    const response = await axiosClient.get<TasksResponse>("/api/v1/tasks");
    return response.data;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

/**
 * Claim a task reward
 * @param {TaskClaimRequest} claimData - The task claim data
 * @returns {Promise<TaskClaimResponse>} The claim response
 */
export const claimTask = async (
  claimData: TaskClaimRequest
): Promise<TaskClaimResponse> => {
  try {
    const response = await axiosClient.post<TaskClaimResponse>(
      "/api/v1/tasks/claim",
      claimData
    );
    return response.data;
  } catch (error) {
    console.error("Error claiming task:", error);
    throw error;
  }
};

/**
 * Get user profile including coin balance and achievements
 * @returns {Promise<UserProfile>} The user profile data
 */
export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const response = await axiosClient.get<UserProfile>("/api/v1/user/profile");
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

/**
 * Get user task completion history
 * @returns {Promise<TaskHistory[]>} The task history
 */
export const getTaskHistory = async (): Promise<TaskHistory[]> => {
  try {
    const response = await axiosClient.get<TaskHistory[]>("/api/v1/tasks/history");
    return response.data;
  } catch (error) {
    console.error("Error fetching task history:", error);
    throw error;
  }
};

/**
 * Get coin transaction history (credits and debits)
 * @returns {Promise<CoinTransaction[]>} The transaction history
 */
export const getCoinTransactions = async (): Promise<CoinTransaction[]> => {
  try {
    const response = await axiosClient.get<CoinTransactionsResponse>("/api/v1/coins/transactions");
    return response.data.transactions;
  } catch (error) {
    console.error("Error fetching coin transactions:", error);
    throw error;
  }
};

/**
 * Get current coin balance
 * @returns {Promise<number>} The current coin balance
 */
export const getCoinBalance = async (): Promise<number> => {
  try {
    const response = await axiosClient.get<CoinBalanceResponse>("/api/v1/coins/balance");
    return response.data.balance;
  } catch (error) {
    console.error("Error fetching coin balance:", error);
    throw error;
  }
};

/**
 * Upgrade to premium using coins
 * @returns {Promise<{success: boolean, message: string}>} The upgrade response
 */
export const upgradeToPremium = async (): Promise<{success: boolean, message: string}> => {
  try {
    const response = await axiosClient.post<{success: boolean, message: string}>("/api/v1/coins/upgrade-premium");
    return response.data;
  } catch (error) {
    console.error("Error upgrading to premium:", error);
    throw error;
  }
};

/**
 * Submit LinkedIn post for verification
 * @param {string} url - The LinkedIn post URL
 * @param {string} body - The content of the LinkedIn post
 * @returns {Promise<{success: boolean, message: string}>} The submission response
 */
export const submitLinkedInPost = async (url: string, body: string): Promise<{success: boolean, message: string}> => {
  try {
    const response = await axiosClient.post<{success: boolean, message: string}>(
      "/api/v1/tasks/linkedin/submit",
      { url, body }
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting LinkedIn post:", error);
    throw error;
  }
};
